import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Flags alineados con <c>Parking.Domain.Master.ParkingProfile</c> (API). */
export const ParkingProfileFlags = {
  Rotativo: 1 << 0,
  Abonados: 1 << 1,
  Acceso: 1 << 2,
  /** Yrix Park por defecto (sin SKU Yrix Acceso). */
  Full: (1 << 0) | (1 << 1),
  /** Yrix Park + Yrix Acceso. */
  All: (1 << 0) | (1 << 1) | (1 << 2)
} as const;

/** Alineado con <c>Parking.Domain.AfipEnvironment</c> (API). */
export const AfipEnvironment = {
  Testing: 0,
  Production: 1
} as const;

export interface Tenant {
  id: number;
  slug: string;
  displayName: string;
  status: string;
  deploymentMode: string;
  shardId: number;
  createdAt: string;
  timeZone: string;
  country?: string;
  /** Idioma de UI del tenant (es, en). */
  language?: string;
  /** Flags de perfil de estacionamiento (rotativo / abonados / acceso). */
  parkingProfile?: number;
  afipEnvironment?: number;
  afipCuit?: string | null;
  afipCertificatePath?: string | null;
  afipPrivateKeyPath?: string | null;
}

export interface CreateTenantWithAdminRequest {
  slug: string;
  displayName: string;
  adminEmail: string;
  adminPassword: string;
  adminName: string;
  adminSurname: string;
  timeZone?: string;
  country?: string;
  /** Idioma de UI (es, en). Si se omite, la API lo deriva del país. */
  language?: string;
  /** Valor numérico de flags (p. ej. <see cref="ParkingProfileFlags.Full"/>). */
  parkingProfile: number;
  afipEnvironment?: number;
  afipCuit?: string;
  afipCertificatePath?: string;
  afipPrivateKeyPath?: string;
}

export interface UpdateTenantAfipRequest {
  afipEnvironment: number;
  afipCuit?: string | null;
  afipCertificatePath?: string | null;
  afipPrivateKeyPath?: string | null;
}

export interface TenantRegistrationResponse {
  userName: string;
  email: string;
  token: string;
  tenantId: number;
}

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  private apiUrl = `${environment.apiBaseUrl}/api/v1`;

  constructor(private http: HttpClient) {}

  getAll(): Observable<Tenant[]> {
    return this.http.get<Tenant[]>(`${this.apiUrl}/Tenant`);
  }

  create(request: CreateTenantWithAdminRequest): Observable<TenantRegistrationResponse> {
    return this.http.post<TenantRegistrationResponse>(`${this.apiUrl}/Tenant`, request);
  }

  updateTimeZone(tenantId: number, timeZone: string): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/Tenant/${tenantId}/timezone`, { timeZone });
  }

  updateParkingProfile(tenantId: number, parkingProfile: number): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/Tenant/${tenantId}/parking-profile`, { parkingProfile });
  }

  updateAfip(tenantId: number, body: UpdateTenantAfipRequest): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/Tenant/${tenantId}/afip`, {
      afipEnvironment: body.afipEnvironment,
      afipCuit: body.afipCuit ?? null,
      afipCertificatePath: body.afipCertificatePath ?? null,
      afipPrivateKeyPath: body.afipPrivateKeyPath ?? null
    });
  }
}
